/**
 * Los ocho campos de la config de deep links, descritos como datos.
 *
 * Cada campo declara qué variable de entorno lo define, si es obligatorio para
 * poder armar un link y qué se rompe cuando falta. Que sea una lista y no ocho
 * bloques de JSX es lo que hace que la tabla, el formulario, el conteo de
 * faltantes y el snippet de entorno salgan todos del mismo lugar y no puedan
 * decir cosas distintas.
 */
import type { DeepLinkConfig } from '@ops/contracts'

export type CampoId =
  | 'plane.baseUrl'
  | 'plane.workspaceSlug'
  | 'plane.projectId'
  | 'close.baseUrl'
  | 'stripe.baseUrl'
  | 'plataforma.baseUrl'
  | 'searchConsole.baseUrl'
  | 'searchConsole.resourceId'

export interface CampoConfig {
  id: CampoId
  /** Nombre del sistema, para agrupar los campos en la pantalla. */
  grupo: string
  label: string
  /** Variable de entorno del Worker que lo define. */
  envVar: string
  /** true = sin este valor el builder devuelve null y el link no se renderiza. */
  requerido: boolean
  /** Qué se rompe si falta. Va en el estado vacío, no es copy de relleno. */
  rompe: string
  ejemplo: string
}

export const CAMPOS: readonly CampoConfig[] = [
  {
    id: 'plane.baseUrl',
    grupo: 'Plane',
    label: 'URL base',
    envVar: 'PLANE_BASE_URL',
    requerido: true,
    rompe: 'Los links a seguimientos apuntarían a un dominio equivocado.',
    ejemplo: 'https://app.plane.so',
  },
  {
    id: 'plane.workspaceSlug',
    grupo: 'Plane',
    label: 'Workspace',
    envVar: 'PLANE_WORKSPACE_SLUG',
    requerido: true,
    rompe: 'Sin workspace no se puede armar la URL de ningún work item.',
    ejemplo: 'laburen',
  },
  {
    id: 'plane.projectId',
    grupo: 'Plane',
    label: 'Proyecto',
    envVar: 'PLANE_PROJECT_ID',
    requerido: true,
    rompe: 'Todos los seguimientos salen como "sin vincular" en el panel.',
    ejemplo: 'ops-panel',
  },
  {
    id: 'close.baseUrl',
    grupo: 'Close',
    label: 'URL base',
    envVar: 'CLOSE_BASE_URL',
    requerido: true,
    rompe: 'Los links a leads y oportunidades apuntarían a otro dominio.',
    ejemplo: 'https://app.close.com',
  },
  {
    id: 'stripe.baseUrl',
    grupo: 'Stripe',
    label: 'URL base',
    envVar: 'STRIPE_BASE_URL',
    requerido: true,
    rompe: 'Los links a customers y suscripciones apuntarían a otro dominio.',
    ejemplo: 'https://dashboard.stripe.com',
  },
  {
    id: 'plataforma.baseUrl',
    grupo: 'Plataforma Laburen',
    label: 'URL base',
    envVar: 'PLATAFORMA_BASE_URL',
    requerido: true,
    rompe: 'Los links a fichas de cliente y de empleado digital se rompen.',
    ejemplo: 'https://app.laburen.com',
  },
  {
    id: 'searchConsole.baseUrl',
    grupo: 'Search Console',
    label: 'URL base',
    envVar: 'SEARCH_CONSOLE_BASE_URL',
    requerido: true,
    rompe: 'El informe de rendimiento abriría en un dominio equivocado.',
    ejemplo: 'https://search.google.com/search-console',
  },
  {
    id: 'searchConsole.resourceId',
    grupo: 'Search Console',
    label: 'Propiedad',
    envVar: 'SEARCH_CONSOLE_RESOURCE_ID',
    requerido: true,
    rompe: 'Ninguna query de SEO puede linkear a su informe.',
    ejemplo: 'sc-domain:laburen.com',
  },
]

/** Orden en que se agrupan los campos en pantalla. */
export const GRUPOS: readonly string[] = [
  'Plane',
  'Close',
  'Stripe',
  'Plataforma Laburen',
  'Search Console',
]

export function valorDeCampo(cfg: DeepLinkConfig, id: CampoId): string {
  switch (id) {
    case 'plane.baseUrl':
      return cfg.plane.baseUrl
    case 'plane.workspaceSlug':
      return cfg.plane.workspaceSlug
    case 'plane.projectId':
      return cfg.plane.projectId
    case 'close.baseUrl':
      return cfg.close.baseUrl
    case 'stripe.baseUrl':
      return cfg.stripe.baseUrl
    case 'plataforma.baseUrl':
      return cfg.plataforma.baseUrl
    case 'searchConsole.baseUrl':
      return cfg.searchConsole.baseUrl
    case 'searchConsole.resourceId':
      return cfg.searchConsole.resourceId
  }
}

/** Devuelve una config nueva con un campo cambiado. No muta la que recibe. */
export function conCampo(
  cfg: DeepLinkConfig,
  id: CampoId,
  valor: string,
): DeepLinkConfig {
  switch (id) {
    case 'plane.baseUrl':
      return { ...cfg, plane: { ...cfg.plane, baseUrl: valor } }
    case 'plane.workspaceSlug':
      return { ...cfg, plane: { ...cfg.plane, workspaceSlug: valor } }
    case 'plane.projectId':
      return { ...cfg, plane: { ...cfg.plane, projectId: valor } }
    case 'close.baseUrl':
      return { ...cfg, close: { baseUrl: valor } }
    case 'stripe.baseUrl':
      return { ...cfg, stripe: { baseUrl: valor } }
    case 'plataforma.baseUrl':
      return { ...cfg, plataforma: { baseUrl: valor } }
    case 'searchConsole.baseUrl':
      return {
        ...cfg,
        searchConsole: { ...cfg.searchConsole, baseUrl: valor },
      }
    case 'searchConsole.resourceId':
      return {
        ...cfg,
        searchConsole: { ...cfg.searchConsole, resourceId: valor },
      }
  }
}

/** Campos obligatorios que están vacíos: los que hoy rompen un link. */
export function camposVacios(cfg: DeepLinkConfig): CampoConfig[] {
  return CAMPOS.filter(
    (campo) =>
      campo.requerido && valorDeCampo(cfg, campo.id).trim().length === 0,
  )
}

/**
 * Las variables de entorno equivalentes a una config, para pegar en el entorno
 * del Worker. Es la salida del editor: el panel no persiste esto (§ ver
 * `DeepLinksPanel`), lo entrega listo para aplicar.
 */
export function envSnippet(cfg: DeepLinkConfig): string {
  return CAMPOS.map(
    (campo) => `${campo.envVar}=${valorDeCampo(cfg, campo.id).trim()}`,
  ).join('\n')
}

/** true si la config editada difiere de la que sirve el servidor. */
export function hayCambios(a: DeepLinkConfig, b: DeepLinkConfig): boolean {
  return CAMPOS.some(
    (campo) => valorDeCampo(a, campo.id) !== valorDeCampo(b, campo.id),
  )
}
